import * as tf from '@tensorflow/tfjs';
import Config from './config';
import Node from './node';
import PredictionStore from './predictionStore';
import SgfUtil from './sgf/sgfUtil';

class Engine {
  constructor() {
    this.name = "ENGINE";
    this.url = "MODEL_URL";
    this.boardSize = 19;
    this.model = undefined;
    this.predictionStore = undefined;
    this.onPredict = undefined;
    this._history = undefined;
    this.rootNode = undefined;
    this.doPonder = true; // true if do ponder
    this.ponderTask = undefined;
    this.threads = 1;  // number of threads
    this.nodeCounter = 0; // number of expanded node for each board state
    this.startTime = 0; // start ponder time
    this.updatePeriod = 1000; // (ms.) print prediction period
    this.updateTime = 0; // last update (print prediction) time
    this.predict = this.predict.bind(this);
    this.ponder = this.ponder.bind(this);
  }

  get models() {
    return [];
  }

  get modelName() {
    let name = Config.getInstance().get("model");
    if (name) {
      return name;
    }
    if (this.models.length) {
      return this.models[0];
    }
    return "undefined";
  }

  async onLoad() {
    // this.model.summary();
    const name = this.name + "-" + this.boardSize + "-" + this.modelName;
    this.predictionStore = new PredictionStore(name);
    if (this.doPonder && this.model && this.history) {
      await this.createRootNode();
      this.startTime = Date.now();
      this.startPonder();
    }
  }

  get history() {
    return this._history;
  }

  set history(history) {
    if (!history || !history.length) {
      return;
    }
    const currentBoardModel = (this.history && this.history.length)
      ? this.history[this.history.length - 1]
      : undefined;
    const currentBoardModelHash = currentBoardModel
      ? currentBoardModel.hash
      : undefined;
    const newBoardModel = history[history.length - 1];
    const newBoardModelHash = newBoardModel.hash;
    if (currentBoardModelHash === newBoardModelHash
      && (this.ponderTask || this.nodeCounter >= Config.getInstance().nodesPerMove)) {
      return;
    }
    const isPreviousState = (this._history && this._history.length > 1 &&
      newBoardModel === this._history[this._history.length - 2]);
    this._history = history;
    if (this.doPonder && this.model) {
      this.nodeCounter = 0;
      this.startTime = Date.now();
      if (currentBoardModelHash !== newBoardModelHash) {
        if (this.isNextState(history, currentBoardModel, newBoardModel)) {
          const move = SgfUtil.vertexToIndex(
            newBoardModel.lastMove, newBoardModel.boardSize);
          this.rootNode = this.rootNode.children[move];
          this.startPonder();
        } else if (isPreviousState) {
          const childNode = this.rootNode;
          this.createRootNode().then(() => {
            const value = childNode.meanEvaluation * -1;
            this.rootNode.addChild(childNode);
            this.rootNode.visitCount++;
            this.rootNode.evaluation += value;
            let visits = PredictionStore.MIN_VISITS_TO_ADJUST_PP;
            if (visits > childNode.visitCount) {
              visits = childNode.visitCount;
            }
            this.rootNode.edges[childNode.lastMove].totalActionValue += value * visits;
            this.rootNode.edges[childNode.lastMove].visitCount = visits;
            this.startPonder();  
          });
        } else if (this.predictionStore) {
          this.createRootNode().then(() => {
            this.startPonder();
          });          
        }
        // console.log("c = " + this.rootNode.c);
      } else if (this.rootNode) {
        this.startPonder();
      }
    }
  }

  isNextState(history, currentBoardModel, newBoardModel) {
    if (history.length < 2) {
      return false;
    } else if (currentBoardModel !== history[history.length - 2]) {
      return false;
    } else if (!this.rootNode || !newBoardModel.lastMove) {
      return false;
    }
    const move = SgfUtil.vertexToIndex(
      newBoardModel.lastMove, newBoardModel.boardSize);
    return this.rootNode.hasChild(move);
  }

  async createRootNode() {
    const boardModel = this.history[this.history.length - 1];
    const key = PredictionStore.getKey(this.history);
    const [policyOutputPromise, valueOutputPromise] = await this.predict(key, this.history);
    const node = new Node(key, boardModel, await policyOutputPromise, await valueOutputPromise);
    this.rootNode = node;
  }

  async startPonder() {
    if (!this.ponderTask) {
      if (this.predictionStore) {
        this.predictionStore.stopShrink();
      }
      this.ponderTask = setTimeout(this.ponder, 0);
    }
  }

  stopPonder() {
    if (this.ponderTask) {
      clearTimeout(this.ponderTask);
      console.log("Stop ponder: " + this.ponderTask);
    }
    this.ponderTask = undefined;
    if (this.predictionStore) {
      this.predictionStore.flush();
      this.predictionStore.shrink();
    }
    console.log("tf.memory().numTensors = " + tf.memory().numTensors);
  }

  async ponder() {
    try {
      const maxNodes = Config.getInstance().nodesPerMove;
      while (this.doPonder && this.history.length && this.model
        && this.nodeCounter < maxNodes) {
        const expansions = [];
        // async/await is not multi-thread, but cannot use WebWorker with WebGL.
        for (let i = 0; i < this.threads; i++) {
          expansions[i] = this.expand(i);
        }
        await Promise.all(expansions);
        const hasLegalMove = expansions.find(expansion => expansion !== null);
        if (!hasLegalMove) {
          console.log("No more legal move. Ponder finished.");
          return;
        }
        if (this.updateTime + this.updatePeriod < Date.now()) {
          this.updateTime = Date.now();
          this.updatePrediction();
        }
      }
      this.updatePrediction();
      this.stopPonder();
    } catch (reason) {
      console.warn(reason);
    };
  }

  async expand(priority) {
    this.nodeCounter++;
    const [node, nextMove, sequence] = await this.rootNode.searchLeaf(priority);
    if (nextMove === null) {
      return null;
    }
    const history = this.getExpandedHistory(node, nextMove, sequence);
    if (!this.model) { // model was changed during ponder
      return null;
    }
    const key = PredictionStore.getKey(history);
    try {
      const [policyOutput, valueOutput] = await this.predict(key, history);
      // console.log(history[history.length - 1].toString());
      // console.log("valueOutput: " + valueOutput);
      const childNode = new Node(key, history[history.length - 1], policyOutput, valueOutput);
      node.addChild(childNode);
      node.backup(nextMove, sequence, childNode.evaluation);
      sequence.push(childNode);
      this.savePredictions(sequence);
      tf.dispose([policyOutput, valueOutput]);
      return node;
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  getExpandedHistory(node, nextMove, sequence) {
    const boardModel = node.play(nextMove);
    const history = this.history.slice();
    history.pop(); // root node is added below.
    sequence.forEach(node => {
      history.push(node.boardModel);
    });
    history.push(boardModel);
    return history;
  }

  async updatePrediction() {
    if (this.doPonder && this.rootNode) {
      const prediction = await this.rootNode.getPrediction();
      if (this.doPonder && this.nodeCounter && prediction) { // double check
        const speed = this.nodeCounter / (Date.now() + 0.001 - this.startTime) * 1000;
        this.threads = Math.min(3, Math.ceil(speed / 2 + 0.001));
        console.log(`Expanded ${this.nodeCounter} nodes. `
          + `${speed.toFixed(1)} nodes/second. hash=${this.rootNode.boardModel.hash}`);
        prediction.speed = speed.toFixed(1);
        this.onPredict(prediction);
      }
    }
  }

  savePredictions(sequence) {
    if (!sequence || !sequence.length) {
      return;
    }
    sequence.forEach(node => {
      this.predictionStore.put(node);
    });
  }
};

export default Engine;